// src/hooks/klines/adapters/bitgetSpot.js
const PROXY  = `${process.env.REACT_APP_BACKEND_URL}/proxy`;
const REST   = "https://api.bitget.com";
const WS_URL = "wss://ws.bitget.com/v2/ws/public";

/* ---------- helpers ---------- */

const TF_MS = {
  "1m":60_000, "3m":180_000, "5m":300_000, "15m":900_000, "30m":1_800_000,
  "1h":3_600_000, "4h":14_400_000, "6h":21_600_000, "12h":43_200_000,
  "1d":86_400_000, "3d":259_200_000, "1w":604_800_000, "1M":2_592_000_000,
};

const toSec = (msOrSec) => {
  const n = Number(msOrSec);
  if (!Number.isFinite(n)) return undefined;
  return n > 2e10 ? Math.floor(n / 1000) : Math.floor(n);
};
const toLwc = (t,o,h,l,c) => ({ time:t, open:+o, high:+h, low:+l, close:+c });

/** Bitget SPOT v2 granularity (обрати внимание: минутки — 1min/3min/5min/15min/30min) */
function mapIntervalToSpotV2(interval) {
  return ({
    "1m":"1min","3m":"3min","5m":"5min","15m":"15min","30m":"30min",
    "1h":"1h","4h":"4h","6h":"6h","12h":"12h",
    "1d":"1day","3d":"3day","1w":"1week","1M":"1M",
  }[interval] || "1min");
}

/** Канал WS для spot — как у mix, но без instType productType */
function mapIntervalToWs(interval) {
  return ({
    "1m":"candle1m","3m":"candle3m","5m":"candle5m","15m":"candle15m","30m":"candle30m",
    "1h":"candle1H","4h":"candle4H","6h":"candle6H","12h":"candle12H",
    "1d":"candle1D","3d":"candle3D","1w":"candle1W","1M":"candle1M",
  }[interval] || "candle1m");
}

/** Символ SPOT: BTCUSDT_SPBL -> BTCUSDT (v2 REST/WS принимают без суффикса) */
function parseBitgetSpotSymbol(raw) {
  const s = String(raw || "");
  return s.replace(/_SPBL$/i, "");
}

/** Округление времени к началу «корзины» (Bitget требует округлять для start/end) */
function snapToBucketMs(tsMs, tfMs) {
  const n = Number(tsMs);
  if (!Number.isFinite(n)) return undefined;
  return Math.floor(n / tfMs) * tfMs;
}

/** Нормализация ответа v2 spot:
 * data: [ [ "ts","o","h","l","c","baseVol","usdtVol","quoteVol" ], ... ]
 */
function normalizeV2(body, limit) {
  const rows = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
  const out = rows.map(a => {
    const ts = toSec(a?.[0]);
    const o = +a?.[1], h = +a?.[2], l = +a?.[3], c = +a?.[4];
    if ([ts,o,h,l,c].every(Number.isFinite)) return toLwc(ts, o,h,l,c);
    return null;
  }).filter(Boolean)
    .sort((x,y)=>x.time-y.time)
    .slice(-Math.max(1, limit || 300));
  return out;
}

/* ---------- REST: история свечей (Bitget v2 /api/v2/spot/market/candles) ---------- */
/**
 * @param {Object} p
 * @param {string} p.symbol    Например "BTCUSDT_SPBL" или "BTCUSDT"
 * @param {string} [p.interval="1m"]
 * @param {number} [p.limit=300]  // max 1000
 * @param {number} [p.startTime]  // ms; опционально
 * @param {number} [p.endTime]    // ms; опционально
 */
export async function bitgetSpotFetchHistory({
  symbol,
  interval = "1m",
  limit = 300,
  startTime,
  endTime,
}) {
  if (!symbol) return [];

  const sym = parseBitgetSpotSymbol(symbol);
  const granularity = mapIntervalToSpotV2(interval);
  const tfMs = TF_MS[interval] || TF_MS["1m"];

  const nowMs = Date.now();
  const endRaw   = Number.isFinite(endTime)   ? +endTime   : nowMs;
  const startRaw = Number.isFinite(startTime) ? +startTime : (endRaw - tfMs * Math.max(1, limit));

  const endMs   = snapToBucketMs(endRaw, tfMs);
  const startMs = snapToBucketMs(startRaw, tfMs);

  const qs = new URLSearchParams();
  qs.set("symbol", sym);
  qs.set("granularity", granularity);
  qs.set("limit", String(Math.min(1000, Math.max(1, limit))));
  if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
    qs.set("startTime", String(startMs));
    qs.set("endTime",   String(endMs));
  }

  const url = `${PROXY}/${REST}/api/v2/spot/market/candles?${qs.toString()}`;
  console.log("[bitgetSpot REST v2] fetch", { url, sym, granularity, limit });

  const r = await fetch(url, { headers: { 'ngrok-skip-browser-warning': '1' } });
  if (!r.ok) {
    // fallback: без start/end
    const qs2 = new URLSearchParams();
    qs2.set("symbol", sym);
    qs2.set("granularity", granularity);
    qs2.set("limit", String(Math.min(1000, Math.max(1, limit))));
    const url2 = `${PROXY}/${REST}/api/v2/spot/market/candles?${qs2.toString()}`;
    console.warn("[bitgetSpot REST v2] retry without start/end", { url2 });
    const r2 = await fetch(url2, { headers: { 'ngrok-skip-browser-warning': '1' } });
    if (!r2.ok) throw new Error(`Bitget SPOT HTTP ${r2.status}`);
    const body2 = await r2.json().catch(()=>[]);
    const out2 = normalizeV2(body2, limit);
    console.log("[bitgetSpot REST v2] ok(retry)", { got: out2.length, first: out2[0], last: out2[out2.length-1] });
    return out2;
  }

  const body = await r.json().catch(()=>[]);
  const out = normalizeV2(body, limit);
  console.log("[bitgetSpot REST v2] ok", { got: out.length, first: out[0], last: out[out.length-1] });
  return out;
}

/* ---------- WS: свечи (v2 публичный сокет) ---------- */
/**
 * @param {Object} p
 * @param {string} p.symbol     "BTCUSDT_SPBL" или "BTCUSDT"
 * @param {string} [p.interval] "1m","5m","1h",...
 * @param {function} p.onCandle (bar)=>void, где bar: {time,open,high,low,close}
 */
export function bitgetSpotOpenWS({ symbol, interval = "1m", onCandle }) {
  const chan = mapIntervalToWs(interval);
  const sym  = parseBitgetSpotSymbol(symbol);
  console.log("[bitgetSpot WS] open", { sym, interval, chan });

  const ws = new WebSocket(WS_URL);
  let subscribed = false;

  const close = () => {
    try {
      if (subscribed && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ op: "unsubscribe", args: [{ instType: "SPOT", channel: chan, instId: sym }] }));
        console.log("[bitgetSpot WS] unsubscribe", { sym, chan });
      }
    } catch {}
    try { ws.close(); } catch {}
    console.log("[bitgetSpot WS] closed");
  };

  ws.onopen = () => {
    const sub = { op: "subscribe", args: [{ instType: "SPOT", channel: chan, instId: sym }] };
    try { ws.send(JSON.stringify(sub)); } catch {}
    subscribed = true;
    console.log("[bitgetSpot WS] subscribed", sub);
  };

  ws.onmessage = (evt) => {
    try {
      const msg = JSON.parse(evt.data);
      if (!msg) return;

      if (msg.event === "subscribe") {
        console.log("[bitgetSpot WS] ack", msg.arg);
        return;
      }
      if (msg.event === "error") {
        console.warn("[bitgetSpot WS] error", msg);
        return;
      }
      if (!msg?.data || !Array.isArray(msg.data)) return;

      // data: [ [ "ts","o","h","l","c", ... ] ]
      for (const row of msg.data) {
        const ts = toSec(row?.[0]);
        const o = +row?.[1], h = +row?.[2], l = +row?.[3], c = +row?.[4];
        if ([ts,o,h,l,c].every(Number.isFinite)) onCandle?.(toLwc(ts, o,h,l,c));
      }
    } catch (e) {
      console.warn("[bitgetSpot WS] parse error", e);
    }
  };

  ws.onerror = (e) => console.warn("[bitgetSpot WS] error", e);
  ws.onclose  = () => console.log("[bitgetSpot WS] onclose");

  return close;
}

export default {
  bitgetSpotFetchHistory,
  bitgetSpotOpenWS,
};
